import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Wrench,
  Plus,
  Trash2,
  Play,
  Save,
  ArrowLeft,
  Code,
  FileJson,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Copy,
  Eye,
} from 'lucide-react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_dashboard/tools/builder')({
  component: ToolBuilderPage,
});

// ============================================================================
// Types
// ============================================================================

type ParameterType = 'string' | 'number' | 'boolean' | 'array' | 'object';

interface ToolParameter {
  id: string;
  name: string;
  type: ParameterType;
  description: string;
  required: boolean;
  defaultValue?: string;
  enumValues?: string[];
  validation?: {
    pattern?: string;
    minLength?: number;
    maxLength?: number;
    minimum?: number;
    maximum?: number;
  };
}

interface ToolDefinition {
  name: string;
  description: string;
  category: string;
  isDestructive: boolean;
  isPopular: boolean;
  parameters: ToolParameter[];
  outputSchema?: Record<string, unknown>;
  implementation: {
    type: 'http' | 'javascript' | 'template';
    config: Record<string, unknown>;
  };
}

interface TestResult {
  success: boolean;
  output?: unknown;
  error?: string;
  duration: number;
}

// ============================================================================
// Constants
// ============================================================================

const CATEGORIES = [
  'crm',
  'communication',
  'database',
  'file_operations',
  'analytics',
  'ai_ml',
  'utilities',
  'integration',
  'security',
  'custom',
];

const PARAMETER_TYPES: { value: ParameterType; label: string }[] = [
  { value: 'string', label: 'String' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'array', label: 'Array' },
  { value: 'object', label: 'Object' },
];

// ============================================================================
// Component
// ============================================================================

function ToolBuilderPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('basic');
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [expandedParams, setExpandedParams] = useState<Set<string>>(new Set());

  // Tool definition state
  const [tool, setTool] = useState<ToolDefinition>({
    name: '',
    description: '',
    category: 'utilities',
    isDestructive: false,
    isPopular: false,
    parameters: [],
    implementation: {
      type: 'http',
      config: {
        method: 'GET',
        url: '',
        headers: {},
        body: '',
      },
    },
  });

  // Test input state
  const [testInput, setTestInput] = useState<Record<string, string>>({});

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleBasicChange = useCallback(
    (field: keyof ToolDefinition, value: unknown) => {
      setTool((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleAddParameter = useCallback(() => {
    const newParam: ToolParameter = {
      id: `param_${Date.now()}`,
      name: '',
      type: 'string',
      description: '',
      required: false,
    };
    setTool((prev) => ({
      ...prev,
      parameters: [...prev.parameters, newParam],
    }));
    setExpandedParams((prev) => new Set([...prev, newParam.id]));
  }, []);

  const handleUpdateParameter = useCallback(
    (id: string, updates: Partial<ToolParameter>) => {
      setTool((prev) => ({
        ...prev,
        parameters: prev.parameters.map((p) =>
          p.id === id ? { ...p, ...updates } : p
        ),
      }));
    },
    []
  );

  const handleRemoveParameter = useCallback((id: string) => {
    setTool((prev) => ({
      ...prev,
      parameters: prev.parameters.filter((p) => p.id !== id),
    }));
    setExpandedParams((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const handleImplementationChange = useCallback(
    (field: string, value: unknown) => {
      setTool((prev) => ({
        ...prev,
        implementation: {
          ...prev.implementation,
          config: {
            ...prev.implementation.config,
            [field]: value,
          },
        },
      }));
    },
    []
  );

  const toggleParameterExpanded = useCallback((id: string) => {
    setExpandedParams((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleTest = useCallback(async () => {
    setIsTesting(true);
    setTestResult(null);

    try {
      const startTime = Date.now();

      // Simulate tool execution
      // In production, this would call the backend to execute the tool
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Mock success for demonstration
      setTestResult({
        success: true,
        output: {
          message: 'Tool executed successfully',
          input: testInput,
          timestamp: new Date().toISOString(),
        },
        duration: Date.now() - startTime,
      });

      toast.success('Tool test completed successfully');
    } catch (error) {
      setTestResult({
        success: false,
        error: (error as Error).message,
        duration: 0,
      });
      toast.error('Tool test failed');
    } finally {
      setIsTesting(false);
    }
  }, [testInput]);

  const handleSave = useCallback(async () => {
    // Validate
    if (!tool.name.trim()) {
      toast.error('Tool name is required');
      setActiveTab('basic');
      return;
    }

    if (!tool.description.trim()) {
      toast.error('Tool description is required');
      setActiveTab('basic');
      return;
    }

    setIsSaving(true);

    try {
      // In production, call API to save the tool
      await new Promise((resolve) => setTimeout(resolve, 1000));

      toast.success('Tool saved successfully');
      navigate({ to: '/tools' });
    } catch (error) {
      toast.error((error as Error).message || 'Failed to save tool');
    } finally {
      setIsSaving(false);
    }
  }, [tool, navigate]);

  const generateSchema = useCallback(() => {
    const schema: Record<string, unknown> = {
      type: 'object',
      properties: {} as Record<string, unknown>,
      required: [] as string[],
    };

    for (const param of tool.parameters) {
      const propSchema: Record<string, unknown> = {
        type: param.type,
        description: param.description,
      };

      if (param.defaultValue) {
        propSchema.default =
          param.type === 'number'
            ? parseFloat(param.defaultValue)
            : param.type === 'boolean'
              ? param.defaultValue === 'true'
              : param.defaultValue;
      }

      if (param.enumValues && param.enumValues.length > 0) {
        propSchema.enum = param.enumValues;
      }

      if (param.validation) {
        if (param.validation.pattern) {
          propSchema.pattern = param.validation.pattern;
        }
        if (param.validation.minLength !== undefined) {
          propSchema.minLength = param.validation.minLength;
        }
        if (param.validation.maxLength !== undefined) {
          propSchema.maxLength = param.validation.maxLength;
        }
        if (param.validation.minimum !== undefined) {
          propSchema.minimum = param.validation.minimum;
        }
        if (param.validation.maximum !== undefined) {
          propSchema.maximum = param.validation.maximum;
        }
      }

      (schema.properties as Record<string, unknown>)[param.name] = propSchema;

      if (param.required) {
        (schema.required as string[]).push(param.name);
      }
    }

    return schema;
  }, [tool.parameters]);

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate({ to: '/tools' })}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Custom Tool Builder</h1>
            <p className="text-muted-foreground">
              Create custom MCP tools without writing code
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleTest} disabled={isTesting || !tool.name}>
            {isTesting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Play className="mr-2 h-4 w-4" />
            )}
            Test
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save Tool
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left: Tool Configuration */}
        <div className="lg:col-span-2 space-y-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="parameters">Parameters</TabsTrigger>
              <TabsTrigger value="implementation">Implementation</TabsTrigger>
              <TabsTrigger value="test">Test</TabsTrigger>
            </TabsList>

            {/* Basic Info Tab */}
            <TabsContent value="basic" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Tool Information</CardTitle>
                  <CardDescription>
                    Define the basic information about your custom tool
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-2">
                    <Label htmlFor="name">Tool Name *</Label>
                    <Input
                      id="name"
                      placeholder="e.g., get_customer_orders"
                      value={tool.name}
                      onChange={(e) => handleBasicChange('name', e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Use snake_case for tool names (e.g., get_data, create_user)
                    </p>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="description">Description *</Label>
                    <Textarea
                      id="description"
                      placeholder="Describe what this tool does and when to use it..."
                      value={tool.description}
                      onChange={(e) => handleBasicChange('description', e.target.value)}
                      rows={3}
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="category">Category</Label>
                    <Select
                      value={tool.category}
                      onValueChange={(value) => handleBasicChange('category', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((cat) => (
                          <SelectItem key={cat} value={cat}>
                            {cat.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Destructive Action</Label>
                      <p className="text-xs text-muted-foreground">
                        Mark if this tool can modify or delete data
                      </p>
                    </div>
                    <Switch
                      checked={tool.isDestructive}
                      onCheckedChange={(checked) =>
                        handleBasicChange('isDestructive', checked)
                      }
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Parameters Tab */}
            <TabsContent value="parameters" className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Input Parameters</CardTitle>
                      <CardDescription>
                        Define the parameters this tool accepts
                      </CardDescription>
                    </div>
                    <Button onClick={handleAddParameter}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add Parameter
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {tool.parameters.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Wrench className="mx-auto h-12 w-12 mb-4 opacity-50" />
                      <p>No parameters defined yet.</p>
                      <p className="text-sm">Add parameters to define the tool's input schema.</p>
                    </div>
                  ) : (
                    tool.parameters.map((param, index) => (
                      <Card key={param.id}>
                        <CardHeader className="py-3 cursor-pointer" onClick={() => toggleParameterExpanded(param.id)}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">#{index + 1}</Badge>
                              <span className="font-medium">
                                {param.name || 'Unnamed parameter'}
                              </span>
                              {param.required && (
                                <Badge variant="secondary">Required</Badge>
                              )}
                              <Badge variant="outline">{param.type}</Badge>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveParameter(param.id);
                                }}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                              {expandedParams.has(param.id) ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </div>
                          </div>
                        </CardHeader>
                        {expandedParams.has(param.id) && (
                          <CardContent className="pt-0 space-y-4">
                            <div className="grid gap-4 sm:grid-cols-2">
                              <div className="grid gap-2">
                                <Label>Parameter Name *</Label>
                                <Input
                                  placeholder="e.g., customer_id"
                                  value={param.name}
                                  onChange={(e) =>
                                    handleUpdateParameter(param.id, { name: e.target.value })
                                  }
                                />
                              </div>
                              <div className="grid gap-2">
                                <Label>Type</Label>
                                <Select
                                  value={param.type}
                                  onValueChange={(value) =>
                                    handleUpdateParameter(param.id, {
                                      type: value as ParameterType,
                                    })
                                  }
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {PARAMETER_TYPES.map((type) => (
                                      <SelectItem key={type.value} value={type.value}>
                                        {type.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>

                            <div className="grid gap-2">
                              <Label>Description</Label>
                              <Textarea
                                placeholder="Describe this parameter..."
                                value={param.description}
                                onChange={(e) =>
                                  handleUpdateParameter(param.id, {
                                    description: e.target.value,
                                  })
                                }
                                rows={2}
                              />
                            </div>

                            <div className="grid gap-4 sm:grid-cols-2">
                              <div className="grid gap-2">
                                <Label>Default Value</Label>
                                <Input
                                  placeholder="Optional default"
                                  value={param.defaultValue || ''}
                                  onChange={(e) =>
                                    handleUpdateParameter(param.id, {
                                      defaultValue: e.target.value,
                                    })
                                  }
                                />
                              </div>
                              <div className="flex items-center justify-between">
                                <div className="space-y-0.5">
                                  <Label>Required</Label>
                                  <p className="text-xs text-muted-foreground">
                                    Must be provided
                                  </p>
                                </div>
                                <Switch
                                  checked={param.required}
                                  onCheckedChange={(checked) =>
                                    handleUpdateParameter(param.id, { required: checked })
                                  }
                                />
                              </div>
                            </div>

                            <div className="grid gap-2">
                              <Label>Enum Values (comma-separated)</Label>
                              <Input
                                placeholder="e.g., pending, active, completed"
                                value={param.enumValues?.join(', ') || ''}
                                onChange={(e) =>
                                  handleUpdateParameter(param.id, {
                                    enumValues: e.target.value
                                      ? e.target.value.split(',').map((s) => s.trim())
                                      : undefined,
                                  })
                                }
                              />
                            </div>
                          </CardContent>
                        )}
                      </Card>
                    ))
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Implementation Tab */}
            <TabsContent value="implementation" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Implementation</CardTitle>
                  <CardDescription>
                    Define how the tool executes when invoked
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-2">
                    <Label>Implementation Type</Label>
                    <Select
                      value={tool.implementation.type}
                      onValueChange={(value) =>
                        setTool((prev) => ({
                          ...prev,
                          implementation: { ...prev.implementation, type: value as 'http' | 'javascript' | 'template' },
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="http">HTTP Request</SelectItem>
                        <SelectItem value="javascript">JavaScript Function</SelectItem>
                        <SelectItem value="template">Response Template</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {tool.implementation.type === 'http' && (
                    <>
                      <div className="grid gap-4 sm:grid-cols-4">
                        <div className="grid gap-2">
                          <Label>Method</Label>
                          <Select
                            value={(tool.implementation.config.method as string) || 'GET'}
                            onValueChange={(value) =>
                              handleImplementationChange('method', value)
                            }
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="GET">GET</SelectItem>
                              <SelectItem value="POST">POST</SelectItem>
                              <SelectItem value="PUT">PUT</SelectItem>
                              <SelectItem value="PATCH">PATCH</SelectItem>
                              <SelectItem value="DELETE">DELETE</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="grid gap-2 sm:col-span-3">
                          <Label>URL</Label>
                          <Input
                            placeholder="https://api.example.com/endpoint/{{customer_id}}"
                            value={(tool.implementation.config.url as string) || ''}
                            onChange={(e) =>
                              handleImplementationChange('url', e.target.value)
                            }
                          />
                        </div>
                      </div>

                      <div className="grid gap-2">
                        <Label>Headers (JSON)</Label>
                        <Textarea
                          placeholder='{"Content-Type": "application/json"}'
                          value={
                            typeof tool.implementation.config.headers === 'object'
                              ? JSON.stringify(tool.implementation.config.headers, null, 2)
                              : ''
                          }
                          onChange={(e) => {
                            try {
                              handleImplementationChange(
                                'headers',
                                JSON.parse(e.target.value)
                              );
                            } catch {
                              // Allow invalid JSON while typing
                            }
                          }}
                          rows={3}
                          className="font-mono text-sm"
                        />
                      </div>

                      {['POST', 'PUT', 'PATCH'].includes(
                        (tool.implementation.config.method as string) || ''
                      ) && (
                        <div className="grid gap-2">
                          <Label>Request Body (JSON Template)</Label>
                          <Textarea
                            placeholder='{"customerId": "{{customer_id}}", "limit": {{limit}}}'
                            value={(tool.implementation.config.body as string) || ''}
                            onChange={(e) =>
                              handleImplementationChange('body', e.target.value)
                            }
                            rows={5}
                            className="font-mono text-sm"
                          />
                          <p className="text-xs text-muted-foreground">
                            Use {'{{parameter_name}}'} to inject parameter values
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  {tool.implementation.type === 'javascript' && (
                    <div className="grid gap-2">
                      <Label>JavaScript Function</Label>
                      <Textarea
                        placeholder={`// Access parameters via 'params' object
// Return the result or throw an error

const { customer_id, limit } = params;

// Your logic here
return {
  success: true,
  data: { customerId: customer_id }
};`}
                        value={(tool.implementation.config.code as string) || ''}
                        onChange={(e) =>
                          handleImplementationChange('code', e.target.value)
                        }
                        rows={12}
                        className="font-mono text-sm"
                      />
                    </div>
                  )}

                  {tool.implementation.type === 'template' && (
                    <div className="grid gap-2">
                      <Label>Response Template (JSON)</Label>
                      <Textarea
                        placeholder='{"message": "Hello, {{name}}!", "timestamp": "{{$now}}"}'
                        value={(tool.implementation.config.template as string) || ''}
                        onChange={(e) =>
                          handleImplementationChange('template', e.target.value)
                        }
                        rows={8}
                        className="font-mono text-sm"
                      />
                      <p className="text-xs text-muted-foreground">
                        Use {'{{parameter_name}}'} for parameters, {'{{$now}}'} for current timestamp
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Test Tab */}
            <TabsContent value="test" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Test Tool</CardTitle>
                  <CardDescription>
                    Test your tool with sample inputs
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {tool.parameters.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <p>No parameters to test.</p>
                      <p className="text-sm">Add parameters first or run the tool directly.</p>
                    </div>
                  ) : (
                    tool.parameters.map((param) => (
                      <div key={param.id} className="grid gap-2">
                        <Label>
                          {param.name || 'Unnamed'}
                          {param.required && <span className="text-destructive ml-1">*</span>}
                        </Label>
                        <Input
                          placeholder={param.description || `Enter ${param.type}`}
                          value={testInput[param.name] || ''}
                          onChange={(e) =>
                            setTestInput((prev) => ({
                              ...prev,
                              [param.name]: e.target.value,
                            }))
                          }
                        />
                      </div>
                    ))
                  )}
                </CardContent>
                <CardFooter>
                  <Button
                    onClick={handleTest}
                    disabled={isTesting}
                    className="w-full"
                  >
                    {isTesting ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Play className="mr-2 h-4 w-4" />
                    )}
                    Run Test
                  </Button>
                </CardFooter>
              </Card>

              {testResult && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      {testResult.success ? (
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                      ) : (
                        <AlertTriangle className="h-5 w-5 text-destructive" />
                      )}
                      Test Result
                    </CardTitle>
                    <CardDescription>
                      Duration: {testResult.duration}ms
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <pre className="bg-muted p-4 rounded-md overflow-auto text-sm">
                      {testResult.success
                        ? JSON.stringify(testResult.output, null, 2)
                        : testResult.error}
                    </pre>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* Right: Preview */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-4 w-4" />
                Preview
              </CardTitle>
              <CardDescription>
                How your tool will appear in the catalog
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 border rounded-lg space-y-2">
                <div className="flex items-center gap-2">
                  <Wrench className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">
                    {tool.name || 'tool_name'}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {tool.description || 'Tool description will appear here...'}
                </p>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="secondary">
                    {tool.category.replace(/_/g, ' ')}
                  </Badge>
                  {tool.isDestructive && (
                    <Badge variant="destructive">
                      <AlertTriangle className="mr-1 h-3 w-3" />
                      Destructive
                    </Badge>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileJson className="h-4 w-4" />
                JSON Schema
              </CardTitle>
              <CardDescription>
                Generated input schema
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <pre className="bg-muted p-4 rounded-md overflow-auto text-xs max-h-64">
                  {JSON.stringify(generateSchema(), null, 2)}
                </pre>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 right-2"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      JSON.stringify(generateSchema(), null, 2)
                    );
                    toast.success('Schema copied to clipboard');
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Code className="h-4 w-4" />
                MCP Definition
              </CardTitle>
              <CardDescription>
                Tool definition for MCP
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <pre className="bg-muted p-4 rounded-md overflow-auto text-xs max-h-64">
                  {JSON.stringify(
                    {
                      name: tool.name || 'tool_name',
                      description: tool.description || 'Tool description',
                      inputSchema: generateSchema(),
                    },
                    null,
                    2
                  )}
                </pre>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 right-2"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      JSON.stringify(
                        {
                          name: tool.name || 'tool_name',
                          description: tool.description || 'Tool description',
                          inputSchema: generateSchema(),
                        },
                        null,
                        2
                      )
                    );
                    toast.success('MCP definition copied to clipboard');
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete your
              custom tool.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
